import { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import { useAuth } from '@/context/AuthContext';
import {
  useFeatures,
  useUserVotes,
  useVoteFeature,
  useUnvoteFeature,
} from '@/hooks/useFeatures';
import FeatureCard from '@/components/features/FeatureCard';
import FeatureForm from '@/components/features/FeatureForm';
import LoginModal from '@/components/common/LoginModal';
import Disclaimer from '@/components/common/Disclaimer';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function FeaturesPage() {
  usePageTitle('功能请求');

  const { user } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const { data: features, isLoading, error } = useFeatures();
  const { data: userVotes = [] } = useUserVotes();
  const voteFeature = useVoteFeature();
  const unvoteFeature = useUnvoteFeature();

  const votedFeatureIds = new Set(userVotes);

  const handleVote = (featureId: number) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    voteFeature.mutate(featureId);
  };

  const handleUnvote = (featureId: number) => {
    if (!user) return;
    unvoteFeature.mutate(featureId);
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          功能提案
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          提交你想要的功能，投票支持感兴趣的提案
        </Typography>
        <Box sx={{ display: 'inline-block', textAlign: 'left' }}>
          <Disclaimer />
        </Box>
      </Box>

      {/* Form */}
      <FeatureForm isLoggedIn={!!user} onLoginClick={handleLoginClick} />

      {/* List */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        排行榜
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          加载失败: {String(error)}
        </Alert>
      )}

      {!isLoading && features && features.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <Typography>还没有功能提案，成为第一个提交的人吧！</Typography>
        </Box>
      )}

      {!isLoading && features && features.length > 0 && (
        <Stack spacing={2}>
          {features.map((feature, idx) => (
            <FeatureCard
              key={feature.id}
              feature={feature}
              rank={idx + 1}
              hasVoted={votedFeatureIds.has(feature.id)}
              isLoggedIn={!!user}
              onVote={handleVote}
              onUnvote={handleUnvote}
              isVoting={voteFeature.isPending || unvoteFeature.isPending}
            />
          ))}
        </Stack>
      )}

      {/* Login Modal */}
      <LoginModal open={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </Box>
  );
}
